import React, { useEffect, useState } from "react";
import { useReducedMotion } from "motion/react";
import { useMenu } from "context/MenuContext";
import { imageForItem, imageFocalForItem } from "./menuImages";
import SubNav from "components/nav/SubNav";
import "./Menu.css";

const MENUS = [
  { id: "Full", label: "Full Menu", key: "dinner", title: "Dinner" },
  { id: "Wine", label: "Wine List", key: "wine", title: "Wine List" },
  { id: "Lunch", label: "Lunch Specials", key: "lunch", title: "Lunch Specials" },
];

const SMALL_WORDS = new Set([
  "and", "or", "of", "the", "a", "an", "with", "in", "on", "to",
  "de", "del", "della", "di", "con", "e", "la", "al", "alla",
]);

// All-caps menu entries (FOR THE TABLE) become elegant Title Case; mixed-case
// names (wine labels) are left untouched.
function displayName(raw) {
  const s = String(raw == null ? "" : raw);
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (!letters || letters !== letters.toUpperCase()) return s;
  return s
    .toLowerCase()
    .split(/(\s+)/)
    .map((w, i) => {
      if (/^\s+$/.test(w) || w === "") return w;
      if (i > 0 && SMALL_WORDS.has(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join("");
}

function capFirst(text) {
  const s = String(text || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatPrice(priceArray) {
  return priceArray
    .map((p) => (Number.isInteger(p) ? String(p) : p.toFixed(2)))
    .join(" / ");
}

function hasGlutenFree(item, isPasta) {
  return (
    isPasta &&
    Array.isArray(item.options) &&
    item.options.some((o) => o.name?.toLowerCase().includes("gluten free"))
  );
}

// Items priced per named variant (e.g. Lasagna: marinara / meat / spinach)
// carry no base price; each variant's price is its sauce price plus each size
// upcharge, matching the printed menu.
function variantRows(item) {
  if (item.price?.length) return [];
  const sizes = item.sizes?.length > 1 ? item.sizes : [{ price: 0 }];
  return (item.sauces || [])
    .filter((v) => v.name && v.price > 0)
    .map((v) => ({
      name: capFirst(v.name.toLowerCase()),
      price: sizes.map((sz) => v.price + (sz.price || 0)),
    }));
}

function Variants({ item }) {
  const rows = variantRows(item);
  if (!rows.length) return null;
  return (
    <ul className="mx-variants">
      {rows.map((v, i) => (
        <li key={i}>
          {v.name}
          <span className="mx-price">{formatPrice(v.price)}</span>
        </li>
      ))}
    </ul>
  );
}

// Plain wrapper. The scroll-reveal animation was removed: under the app's very
// frequent re-renders plus two sticky backdrop-filtered bars, the animating
// transforms intermittently left content blank. Content now renders statically.
function Reveal({ as = "div", className, children }) {
  const Tag = as;
  return <Tag className={className}>{children}</Tag>;
}

// Defined at module scope (not inside Menu) so their identity stays stable
// across Menu re-renders and they are never remounted.
function Feature({ item, name, image, side, isPasta, reduce, focal }) {
  const gf = hasGlutenFree(item, isPasta);
  return (
    <Reveal className={`mx-feature mx-${side}`} reduce={reduce}>
      <figure className="mx-feature-fig">
        <img
          src={image}
          alt={displayName(name)}
          loading="lazy"
          decoding="async"
          style={focal ? { objectPosition: focal } : undefined}
        />
      </figure>
      <div className="mx-feature-body">
        <h3 className="mx-dish">
          {displayName(name)}
          {gf && <sup className="mx-gf">gf</sup>}
          {item.price?.length > 0 && (
            <span className="mx-price">{formatPrice(item.price)}</span>
          )}
        </h3>
        {item.description && (
          <p className="mx-desc">{capFirst(item.description)}</p>
        )}
        <Variants item={item} />
      </div>
    </Reveal>
  );
}

function ListItem({ item, name, isPasta }) {
  const gf = hasGlutenFree(item, isPasta);
  return (
    <div className="mx-li">
      <p className="mx-li-name">
        {displayName(name)}
        {gf && <sup className="mx-gf">gf</sup>}
        {item.price?.length > 0 && (
          <span className="mx-price">{formatPrice(item.price)}</span>
        )}
      </p>
      {item.description && (
        <p className="mx-li-desc">{capFirst(item.description)}</p>
      )}
      <Variants item={item} />
    </div>
  );
}

function Section({ section, reduce }) {
  const isPasta = section.header?.toLowerCase().includes("pasta");
  const features = [];
  const rest = [];
  (section.items || []).forEach((raw) => {
    const item = typeof raw === "object" ? raw : { name: raw };
    const name = item.name != null ? item.name : raw;
    const image = imageForItem(name);
    if (image)
      features.push({ item, name, image, focal: imageFocalForItem(name) });
    else rest.push({ item, name });
  });

  return (
    <section className="mx-sec">
      <Reveal className="mx-sec-head" reduce={reduce}>
        <h2 className="mx-sec-title">{displayName(section.header)}</h2>
      </Reveal>

      {features.map((f, i) => (
        <Feature
          key={`f-${i}`}
          item={f.item}
          name={f.name}
          image={f.image}
          focal={f.focal}
          isPasta={isPasta}
          side={i % 2 === 0 ? "left" : "right"}
          reduce={reduce}
        />
      ))}

      {rest.length > 0 && (
        <div className="mx-list">
          {rest.map((r, i) => (
            <Reveal key={`l-${i}`} reduce={reduce}>
              <ListItem item={r.item} name={r.name} isPasta={isPasta} />
            </Reveal>
          ))}
        </div>
      )}

      {isPasta && (
        <p className="mx-footnote">
          Whole wheat &amp; gluten-free pasta available
        </p>
      )}
    </section>
  );
}

export default function Menu() {
  const { menu } = useMenu();
  const reduce = useReducedMotion();
  const [currentMenu, setCurrent] = useState(
    () => window.sessionStorage.getItem("currentMenu") || "Full"
  );

  const active = MENUS.find((m) => m.id === currentMenu) || MENUS[0];

  useEffect(() => {
    if (!MENUS.some((m) => m.id === currentMenu)) setCurrent("Full");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectMenu(id) {
    if (id === currentMenu) return;
    window.sessionStorage.setItem("currentMenu", id);
    setCurrent(id);
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }

  const data = menu ? menu[active.key] : null;

  return (
    <div className="mx">
      <SubNav
        variant="tabs"
        ariaLabel="Menus"
        items={MENUS}
        activeId={currentMenu}
        onSelect={selectMenu}
      />

      <div className="mx-doc">
          <header className="mx-hero">
            <Reveal as="h1" className="mx-hero-title" reduce={reduce}>
              {active.title}
            </Reveal>
          </header>

          <main className="mx-main">
            {data ? (
              data.sections.map((section, i) => (
                <Section key={i} section={section} reduce={reduce} />
              ))
            ) : (
              <p className="mx-loading">Setting the table…</p>
            )}
          </main>

          <div className="mx-bottom">
            <button
              className="mx-bottom-link"
              onClick={() =>
                window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" })
              }
            >
              Back to top
            </button>
            {MENUS.filter((m) => m.id !== currentMenu).map((m) => (
              <button
                key={m.id}
                className="mx-bottom-link"
                onClick={() => selectMenu(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
      </div>
    </div>
  );
}
